import {
  ApSelect,
  ApInput,
  ApRadio,
  ApButtonMain,
} from '@alixpartners/ui-components';
import { SettingsNav } from '../SettingsNav';
import { ApiContext } from 'context/ApiContext';
import { isEqual } from 'lodash';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ISettings, ISettingsResponse } from 'utils/types';
import {
  createPeriodsFromRange,
  getSelectedIds,
  MONTHS_INDEXES,
  getPeriodStr,
} from '../settings.utils';
import {
  ContentTitle,
  RadioContainer,
  RadioLabel,
  Section,
  SectionText,
  SectionTitle,
  SectionDescription,
  SectionForm,
  SelectContainer,
  SelectLabel,
} from './Preferences.styles';

import { Content, ScrollContainer } from '../Settings.styles';
import { PERMISSIONS } from 'utils/constants';

interface IPreferenceProps extends ISettingsResponse {
  location: any;
  getSettings: () => Promise<any>;
  setSettings: (settings: ISettings) => Promise<any>;
  permissions: string[];
}

class PreferencesView extends React.Component<IPreferenceProps, ISettings> {
  state = {
    // Selected values
    currencyId: 0,
    unitId: 0,
    decimalId: 0,
    calendarYear: {
      startYear: 0,
      endYear: 0,
      startPeriod: 0,
      endPeriod: 0,
    },
    clientName: 'Client Name',
  };

  componentDidMount() {
    this.setState({
      ...getSelectedIds(this.props),
    });
  }

  componentDidUpdate(prevProps: ISettingsResponse) {
    if (!isEqual(prevProps, this.props)) {
      this.setState({
        ...getSelectedIds(this.props),
      });
    }
  }

  isValid = () => {
    const { clientName } = this.state;

    return !!clientName;
  };

  changeClientName = (clientName: string) => {
    this.setState({ clientName });
  };

  handleMonthChange = (value: string) => {
    const { calendarYear } = this.state;

    this.setState({
      calendarYear: {
        ...calendarYear,
        startPeriod: Number(value),
        endPeriod: Number(value) - 1 >= 1 ? Number(value) - 1 : 12,
        endYear:
          Number(value) === 1
            ? calendarYear!.startYear
            : calendarYear!.startYear + 1,
      },
    });
  };

  handleFinancialYearClick = () => {
    const { calendarYear } = this.state;

    this.setState({
      calendarYear: {
        ...calendarYear,
        startPeriod: 4,
        endPeriod: 3,
        endYear: calendarYear.startYear + 1,
      },
    });
  };

  handleCalendarYearClick = () => {
    const { calendarYear } = this.state;
    const { availableRange } = this.props;
    const yearToSet =
      calendarYear.startYear < availableRange.startYear
        ? availableRange.startYear
        : calendarYear.startYear;

    this.setState({
      calendarYear: {
        ...calendarYear,
        startPeriod: 1,
        endPeriod: 12,
        startYear: yearToSet,
        endYear: yearToSet,
      },
    });
  };

  render() {
    const {
      availableRange,
      currencies = [],
      units = [],
      decimals = [],
      getSettings,
      setSettings,
      permissions,
    } = this.props;
    const { calendarYear, currencyId, unitId, decimalId, clientName } =
      this.state;
    const availableYears = createPeriodsFromRange(
      availableRange,
      calendarYear?.startPeriod
    );
    const isNoChanges = isEqual(
      {
        calendarYear,
        currencyId,
        unitId,
        decimalId,
        clientName,
      },
      getSelectedIds(this.props)
    );
    const isDisabled = isNoChanges || !this.isValid();
    const isCalendarYear = calendarYear?.startPeriod === 1;
    const hasWritePermissions = permissions.includes(
      PERMISSIONS.PREFERENCES_WRITE
    );

    return (
      <React.Fragment>
        <SettingsNav
          right={
            hasWritePermissions && (
              <ApButtonMain
                onClick={() => {
                  setSettings({
                    calendarYear,
                    currencyId,
                    unitId,
                    decimalId,
                    clientName,
                  }).then(getSettings);
                }}
                disabled={isDisabled}
              >
                Apply Settings
              </ApButtonMain>
            )
          }
        />
        <ScrollContainer>
          <Content>
            <ContentTitle>General</ContentTitle>

            <Section>
              <SectionText>
                <SectionTitle>Numbers and values</SectionTitle>
                <SectionDescription>
                  Choose the default currency, unit and number of decimal places
                  for values
                </SectionDescription>
              </SectionText>

              <SectionForm>
                <SelectContainer>
                  <SelectLabel>Currency display symbol</SelectLabel>
                  <ApSelect
                    name="currency-select"
                    onChange={(currencyId) => {
                      this.setState({ currencyId: +currencyId });
                    }}
                    options={currencies.map((c) => ({
                      value: String(c.id),
                      label: c.value,
                    }))}
                    disabled={currencies.length === 0}
                    value={`${currencyId}`}
                  />
                </SelectContainer>
                <SelectContainer>
                  <SelectLabel>Unit</SelectLabel>
                  <ApSelect
                    name="unit-select"
                    onChange={(unitId) => {
                      this.setState({ unitId: +unitId });
                    }}
                    options={units.map((c) => ({
                      value: String(c.id),
                      label: c.value,
                    }))}
                    placeholder=""
                    disabled={units.length === 0}
                    value={`${unitId}`}
                  />
                </SelectContainer>
                <SelectContainer>
                  <SelectLabel>Decimals</SelectLabel>
                  <ApSelect
                    name="decimals-select"
                    onChange={(decimalId) => {
                      this.setState({ decimalId: +decimalId });
                    }}
                    options={decimals.map((c) => ({
                      value: String(c.id),
                      label: c.value,
                    }))}
                    disabled={decimals.length === 0}
                    value={`${decimalId}`}
                  />
                </SelectContainer>
              </SectionForm>
            </Section>
            <Section>
              <SectionText>
                <SectionTitle>Year settings</SectionTitle>
                <SectionDescription>
                  Choose whether to see data by calendar or financial year.
                </SectionDescription>
              </SectionText>
              <SectionForm>
                <RadioContainer>
                  <ApRadio
                    checked={isCalendarYear}
                    id="Calendar year"
                    theme="light"
                    type="radio"
                    onChange={this.handleCalendarYearClick}
                  >
                    <RadioLabel>Calendar year (January - December)</RadioLabel>
                  </ApRadio>
                </RadioContainer>
                <RadioContainer>
                  <ApRadio
                    checked={!isCalendarYear}
                    id="Financial Year"
                    theme="light"
                    type="radio"
                    onChange={this.handleFinancialYearClick}
                  >
                    <RadioLabel>Financial year starts in</RadioLabel>
                  </ApRadio>
                  <ApSelect
                    name="Select start period"
                    onChange={this.handleMonthChange}
                    options={MONTHS_INDEXES}
                    value={String(calendarYear?.startPeriod)}
                  />
                </RadioContainer>
              </SectionForm>
            </Section>
            <Section>
              <SectionText>
                <SectionTitle>Current year settings</SectionTitle>
                <SectionDescription>
                  Choose which year to set as the current year
                </SectionDescription>
              </SectionText>
              <SectionForm>
                {availableYears.map((period) => {
                  const label = getPeriodStr(period);
                  return (
                    <RadioContainer key={label}>
                      <ApRadio
                        id={label}
                        theme="light"
                        type="radio"
                        checked={label === getPeriodStr(calendarYear)}
                        onChange={() => {
                          this.setState({ calendarYear: period });
                        }}
                      >
                        <RadioLabel>{label}</RadioLabel>
                      </ApRadio>
                    </RadioContainer>
                  );
                })}
              </SectionForm>
            </Section>
            <Section>
              <SectionText>
                <SectionTitle>Client name</SectionTitle>
                <SectionDescription>
                  Set what client name to display on benchmarks
                </SectionDescription>
              </SectionText>

              <SectionForm>
                <SelectContainer>
                  <SelectLabel>Client name</SelectLabel>
                  <ApInput
                    onChange={this.changeClientName}
                    value={clientName}
                  />
                </SelectContainer>
              </SectionForm>
            </Section>
          </Content>
        </ScrollContainer>
      </React.Fragment>
    );
  }
}

export const Preferences = (props: RouteComponentProps) => (
  <ApiContext.Consumer>
    {({ getSettings, settings, setSettings, permissions }) => (
      <PreferencesView
        {...props}
        {...settings}
        permissions={permissions}
        getSettings={getSettings}
        setSettings={setSettings}
      />
    )}
  </ApiContext.Consumer>
);
